import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export type SecondaryPanel = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
};

interface EntityDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  headerActions?: React.ReactNode;
  widthClassName?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** When provided, a second panel slides in to the right of the primary. */
  secondary?: SecondaryPanel;
}

export default function EntityDrawer({
  open,
  onClose,
  title,
  subtitle,
  headerActions,
  widthClassName = 'max-w-2xl',
  children,
  footer,
  secondary,
}: EntityDrawerProps) {
  // Backdrop / Escape closes the secondary first, then the primary.
  function handleDialogClose() {
    if (secondary?.open) {
      secondary.onClose();
    } else {
      onClose();
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleDialogClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            {/* Remove left-padding so combined panels can use full screen width */}
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                {/* Outer container: flex row holding primary + optional secondary */}
                <Dialog.Panel className="pointer-events-auto flex h-full overflow-hidden">
                  {/* ══ PRIMARY PANEL ══ */}
                  <div
                    className={`relative flex h-full flex-col bg-surface-container-lowest shadow-float ${widthClassName} w-screen flex-shrink-0`}
                  >
                    <div className="border-b border-outline px-6 py-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <Dialog.Title className="text-lg font-semibold text-on-surface">
                            {title}
                          </Dialog.Title>
                          {subtitle && (
                            <p className="mt-1 text-sm text-on-surface-variant">
                              {subtitle}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {headerActions}
                          <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg p-1 text-on-surface-variant hover:bg-surface-container-low"
                            aria-label="Close drawer"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-4">
                      {children}
                    </div>

                    {footer && (
                      <div className="border-t border-outline px-6 py-4">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {footer}
                        </div>
                      </div>
                    )}

                    {/* Dim overlay when secondary is active */}
                    <div
                      aria-hidden="true"
                      className={`absolute inset-0 z-10 bg-black/20 transition-opacity duration-300 ${
                        secondary?.open
                          ? 'pointer-events-auto cursor-pointer opacity-100'
                          : 'pointer-events-none opacity-0'
                      }`}
                      onClick={() => secondary?.open && secondary.onClose()}
                    />
                  </div>

                  {/* ══ SECONDARY PANEL ══ */}
                  {secondary && (
                    <div
                      className="overflow-hidden flex-shrink-0"
                      style={{
                        width: secondary.open ? '480px' : '0px',
                        transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {/* Inner div keeps a fixed width so content doesn't reflow during animation */}
                      <div className="flex h-full w-[480px] flex-col border-l border-outline bg-surface-container-lowest">
                        <div className="border-b border-outline px-6 py-4">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h3 className="text-lg font-semibold text-on-surface">
                                {secondary.title}
                              </h3>
                              {secondary.subtitle && (
                                <p className="mt-1 text-sm text-on-surface-variant">
                                  {secondary.subtitle}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={secondary.onClose}
                              className="rounded-lg p-1 text-on-surface-variant hover:bg-surface-container-low"
                              aria-label="Close panel"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-4">
                          {secondary.children}
                        </div>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
